import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Printer, Loader2, Search, Download } from "lucide-react";
import { inr, fmtDate, exportCsv, daysAgo, today } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export function PageHeader({ title, subtitle, children }) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-3 fade-up">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight sm:text-3xl" data-testid="page-title">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

export function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="np-label">{label}</span>
      {children}
    </label>
  );
}

export function Select({ options, placeholder, ...p }) {
  return (
    <select className="np-input" {...p}>
      {placeholder && <option value="">{placeholder}</option>}
      {options.map((o) => (typeof o === "string" ? <option key={o} value={o}>{o}</option> : <option key={o.value} value={o.value}>{o.label}</option>))}
    </select>
  );
}

export function Btn({ loading, children, variant = "primary", className = "", ...p }) {
  const styles = {
    primary: "bg-[#0D1B3E] text-white hover:bg-[#16295a]",
    dark: "bg-slate-900 text-white hover:bg-slate-800",
    green: "bg-emerald-600 text-white hover:bg-emerald-700",
    outline: "border border-slate-300 bg-white text-slate-800 hover:bg-slate-50",
    red: "bg-red-600 text-white hover:bg-red-700",
  };
  return (
    <button
      className={`inline-flex h-10 items-center justify-center gap-2 rounded-md px-4 text-sm font-semibold transition-[background-color,transform] active:scale-[0.98] disabled:opacity-60 ${styles[variant]} ${className}`}
      disabled={loading || p.disabled}
      {...p}
    >
      {loading && <Loader2 className="h-4 w-4 animate-spin" />}
      {children}
    </button>
  );
}

export function Card({ title, children, className = "", action }) {
  return (
    <section className={`np-card p-5 fade-up ${className}`}>
      {title && (
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-display text-base font-semibold">{title}</h3>
          {action}
        </div>
      )}
      {children}
    </section>
  );
}

const STATUS = {
  SUCCESS: "bg-emerald-50 text-emerald-700 border-emerald-200",
  APPROVED: "bg-emerald-50 text-emerald-700 border-emerald-200",
  VERIFIED: "bg-emerald-50 text-emerald-700 border-emerald-200",
  PENDING: "bg-amber-50 text-amber-700 border-amber-200",
  OPEN: "bg-amber-50 text-amber-700 border-amber-200",
  "IN PROGRESS": "bg-blue-50 text-blue-700 border-blue-200",
  FAILED: "bg-red-50 text-red-700 border-red-200",
  REJECTED: "bg-red-50 text-red-700 border-red-200",
  REFUNDED: "bg-purple-50 text-purple-700 border-purple-200",
  CANCELLED: "bg-slate-100 text-slate-600 border-slate-200",
  CLOSED: "bg-slate-100 text-slate-600 border-slate-200",
};

export function StatusBadge({ status }) {
  return <span className={`inline-block rounded border px-2 py-0.5 text-[10px] font-bold tracking-wide ${STATUS[status] || STATUS.CLOSED}`} data-testid="status-badge">{status}</span>;
}

export function MpinField({ value, onChange, testid = "mpin-input" }) {
  return (
    <Field label="MPIN">
      <input type="password" inputMode="numeric" maxLength={4} className="np-input font-mono tracking-[0.5em]" placeholder="••••"
        value={value} onChange={(e) => onChange(e.target.value.replace(/\D/g, ""))} data-testid={testid} />
    </Field>
  );
}

export function DataTable({ columns, rows, empty = "No records found", testid = "data-table", onRow }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white" data-testid={testid}>
      <table className="w-full text-left text-[13px]">
        <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
          <tr>{columns.map((c) => <th key={c.label} className="whitespace-nowrap px-3 py-2.5 font-semibold">{c.label}</th>)}</tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={columns.length} className="px-3 py-10 text-center text-slate-400" data-testid="table-empty">{empty}</td></tr>
          )}
          {rows.map((r, i) => (
            <tr key={r.id || i} className={`border-t border-slate-100 hover:bg-slate-50/70 ${onRow ? "cursor-pointer" : ""}`} onClick={onRow ? () => onRow(r) : undefined} data-testid={`table-row-${i}`}>
              {columns.map((c) => <td key={c.label} className="whitespace-nowrap px-3 py-2.5">{c.render ? c.render(r) : r[c.key]}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function FilterBar({ onSearch, loading, children, exportRows, exportName, exportCols }) {
  const [f, setF] = useState({ date_from: daysAgo(30), date_to: today() });
  return (
    <div className="np-card mb-4 flex flex-wrap items-end gap-3 p-4">
      <Field label="From Date"><input type="date" className="np-input" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} data-testid="filter-from-date" /></Field>
      <Field label="To Date"><input type="date" className="np-input" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} data-testid="filter-to-date" /></Field>
      {children}
      <Btn onClick={() => onSearch(f)} loading={loading} data-testid="filter-search-button"><Search className="h-4 w-4" />Search</Btn>
      {exportRows && (
        <Btn variant="outline" onClick={() => exportCsv(exportName, exportRows, exportCols)} data-testid="export-excel-button"><Download className="h-4 w-4" />Export</Btn>
      )}
    </div>
  );
}

const HIDDEN = ["statement", "upi", "bank_balance", "provider", "provider_response", "provider_charge", "payment_id", "account_enc", "vendor_id", "route",
  "submitted_at", "responded_at", "last_checked", "charge_rule", "batch_id", "batch_row", "transfer_type"];

export function ReceiptDialog({ txn, onClose }) {
  const { user } = useAuth();
  if (!txn) return null;
  const d = txn.details || {};
  const payout = txn.service === "Payout";
  const rows = payout ? [
    ["Txn ID", txn.txn_id], ["Date", fmtDate(txn.created_at)], ["Beneficiary", d.holder_name], ["A/c No", txn.number],
    ["Bank", txn.operator], ["IFSC", d.ifsc], ["Mode", d.mode], ["UTR", d.utr || (txn.status === "PENDING" ? "Awaiting bank" : "-")],
  ] : [
    ["Txn ID", txn.txn_id], ["Date", fmtDate(txn.created_at)], ["Service", txn.service], ["Number / A/c", txn.number],
    ["Operator / Bank", txn.operator], ...Object.entries(d).filter(([k, v]) => !HIDDEN.includes(k) && v !== "" && v !== null && typeof v !== "object")
      .map(([k, v]) => [k.replace(/_/g, " "), String(v)]),
    ["Operator Ref", txn.operator_ref],
  ];
  if (d.bank_balance !== undefined) rows.push(["A/c Balance", inr(d.bank_balance)]);
  if (txn.surcharge) rows.push(["Charges", inr(txn.surcharge)]);
  const brand = user?.branding?.company_name || user?.shop_name || user?.name;
  return (
    <Dialog open={!!txn} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="receipt-dialog">
        <DialogHeader><DialogTitle className="font-display">Transaction Receipt</DialogTitle></DialogHeader>
        <div className="print-area rounded-lg border border-dashed border-slate-300 bg-white p-4 font-mono text-[12px]">
          <div className="text-center">
            <div className="font-display text-lg font-extrabold" data-testid="receipt-brand">{brand}</div>
            <div className="text-[11px] text-slate-500">{user?.mobile}</div>
          </div>
          <div className="my-3 text-center">
            <div className="text-2xl font-bold" data-testid="receipt-amount">{inr(txn.amount)}</div>
            <StatusBadge status={txn.status} />
          </div>
          <div className="space-y-1 border-t border-dashed border-slate-300 pt-2">
            {rows.map(([k, v]) => (
              <div key={k} className="flex justify-between gap-3"><span className="capitalize text-slate-500">{k}</span><span className="text-right font-semibold">{v || "-"}</span></div>
            ))}
          </div>
          {Array.isArray(d.statement) && (
            <div className="mt-3 border-t border-dashed border-slate-300 pt-2" data-testid="mini-statement">
              <div className="mb-1 font-bold">Mini Statement</div>
              {d.statement.map((s, i) => (
                <div key={i} className="flex justify-between"><span>{s.date} {s.narration}</span><span className={s.type === "CR" ? "text-emerald-700" : "text-red-600"}>{s.type} {s.amount}</span></div>
              ))}
            </div>
          )}
          <div className="mt-3 text-center text-[10px] text-slate-400">Thank you</div>
        </div>
        <Btn onClick={() => window.print()} data-testid="print-receipt-button"><Printer className="h-4 w-4" />Print Receipt</Btn>
      </DialogContent>
    </Dialog>
  );
}

export const txnColumns = (onReceipt) => [
  { label: "Date", render: (r) => <span className="text-slate-500">{fmtDate(r.created_at)}</span>, csv: (r) => r.created_at },
  { label: "Txn ID", key: "txn_id", render: (r) => <span className="font-mono text-xs">{r.txn_id}</span> },
  { label: "Service", key: "service" },
  { label: "Number", key: "number", render: (r) => <span className="font-mono text-xs">{r.number}</span> },
  { label: "Operator", key: "operator" },
  { label: "Amount", key: "amount", render: (r) => <span className="font-mono font-semibold">{inr(r.amount)}</span> },
  { label: "Comm.", key: "commission", render: (r) => <span className="font-mono text-emerald-700">{inr(r.commission)}</span> },
  { label: "Charge", key: "surcharge", render: (r) => <span className="font-mono text-red-600">{inr(r.surcharge)}</span> },
  { label: "TDS", key: "tds", render: (r) => <span className="font-mono">{inr(r.tds)}</span> },
  { label: "Status", key: "status", render: (r) => <StatusBadge status={r.status} /> },
  { label: "UTR / Ref", render: (r) => <span className="font-mono text-xs" data-testid={`utr-${r.txn_id}`}>{r.details?.utr || r.details?.rrn || r.details?.bbps_ref || "-"}</span>, csv: (r) => r.details?.utr || r.details?.rrn || r.details?.bbps_ref || "" },
  ...(onReceipt ? [{ label: "Receipt", render: (r) => <button onClick={() => onReceipt(r)} className="text-xs font-semibold text-[#0D1B3E] hover:underline" data-testid={`receipt-btn-${r.txn_id}`}>View</button> }] : []),
];
